import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface LegalSection {
  title: string;
  content: string;
}

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  sections: LegalSection[];
}

const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({ title, lastUpdated, sections }) => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl font-semibold text-foreground mb-3">{title}</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>

        <div className="space-y-10">
          {sections.map((section, index) => (
            <section key={index} className="prose prose-neutral dark:prose-invert max-w-none">
              <h2 className="text-xl font-semibold text-foreground mb-4">{section.title}</h2>
              <div className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {section.content.split('\n').map((paragraph, pIndex) => {
                  // Handle bullet points
                  if (paragraph.trim().startsWith('•')) {
                    return (
                      <p key={pIndex} className="ml-4 my-1">
                        {formatText(paragraph)}
                      </p>
                    );
                  }
                  // Handle bold headers within content
                  if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**:')) {
                    return (
                      <p key={pIndex} className="font-medium text-foreground mt-4 mb-2">
                        {paragraph.replace(/\*\*/g, '').replace(':', '')}
                      </p>
                    );
                  }
                  // Regular paragraphs
                  if (paragraph.trim()) {
                    return (
                      <p key={pIndex} className="my-3">
                        {formatText(paragraph)}
                      </p>
                    );
                  }
                  return null;
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Footer navigation */}
        <div className="mt-16 pt-8 border-t border-border/40">
          <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
            <Link to="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-foreground transition-colors">
              Terms of Service
            </Link>
            <Link to="/support" className="hover:text-foreground transition-colors">
              Contact Support
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

// Helper to format bold text
function formatText(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="text-foreground font-medium">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export default LegalPageLayout;
